import { useEffect, useRef, useState } from "react";
import { ADD_ITEM_PROGRESS_CHANNEL } from "../../components/addItem/AddItemUtils";

const FALLBACK_TIMEOUT_MS = 120_000; // 2 minutes

export interface AddItemProgressState {
  isAddingItem: boolean;
  hasFailed: boolean;
}

const useAddItemInProgress = (): AddItemProgressState => {
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let channel: BroadcastChannel;
    try {
      channel = new BroadcastChannel(ADD_ITEM_PROGRESS_CHANNEL);
    } catch {
      return;
    }

    const clearFallback = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    channel.onmessage = (event: MessageEvent<{ type: string }>) => {
      const { type } = event.data ?? {};

      if (type === "add-item-started") {
        clearFallback();
        setHasFailed(false);
        setIsAddingItem(true);
        timeoutRef.current = setTimeout(() => {
          try {
            const ch = new BroadcastChannel(ADD_ITEM_PROGRESS_CHANNEL);
            ch.postMessage({ type: "add-item-timed-out" });
            ch.close();
          } catch {
            // BroadcastChannel not supported
          }
          setIsAddingItem(false);
        }, FALLBACK_TIMEOUT_MS);
      } else if (type === "add-item-completed") {
        clearFallback();
        setHasFailed(false);
        setIsAddingItem(false);
      } else if (type === "add-item-failed") {
        clearFallback();
        setHasFailed(true);
        setIsAddingItem(false);
      }
    };

    return () => {
      channel.close();
      clearFallback();
    };
  }, []);

  return { isAddingItem, hasFailed };
};

export default useAddItemInProgress;
